import React from 'react';
import './UpgradeOfferSummary.css';

export type UpgradeOfferPlan = {
  planKey: string;
  label: string | null;
  amount: number;
  discountedAmount?: number | null;
  currency: string;
  interval: string | null;
  intervalCount: number | null;
  checkoutUrl: string;
};

export type UpgradeOfferPresentation = {
  productName?: string | null;
  monthlyAiRetailCreditUsd?: string | null;
  plans?: UpgradeOfferPlan[];
};

function effectiveAmount(plan: UpgradeOfferPlan): number {
  return plan.discountedAmount == null ? plan.amount : plan.discountedAmount;
}

function formatPlanPrice(plan: UpgradeOfferPlan): string {
  const currency = String(plan.currency || 'usd').toUpperCase();
  const amount = effectiveAmount(plan) / 100;
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
  const intervalCount = plan.intervalCount ?? 1;
  const interval = plan.interval || 'purchase';
  const cadence = intervalCount === 1 ? interval : `${intervalCount} ${interval}s`;
  return `${formatted}/${cadence}`;
}

export default function UpgradeOfferSummary({
  offer,
  compact = false,
}: {
  offer: UpgradeOfferPresentation;
  compact?: boolean;
}): React.ReactElement | null {
  const plans = Array.isArray(offer.plans) ? offer.plans : [];
  if (!plans.length && !offer.monthlyAiRetailCreditUsd) return null;

  return (
    <div className={`upgrade-offer-summary${compact ? ' upgrade-offer-summary--compact' : ''}`}>
      {plans.length ? (
        <div className="upgrade-offer-summary__prices" aria-label="Upgrade plan prices">
          {plans.map((plan) => (
            <span key={plan.planKey} className="upgrade-offer-summary__price">
              <span>{plan.label || plan.planKey}</span>
              <strong>{formatPlanPrice(plan)}</strong>
            </span>
          ))}
        </div>
      ) : null}
      {offer.monthlyAiRetailCreditUsd ? (
        <p className="upgrade-offer-summary__allowance">
          ${offer.monthlyAiRetailCreditUsd} in monthly AI credits
        </p>
      ) : null}
    </div>
  );
}
